import { _ } from "../polyglot/polyglot.js";
import { makeComputationApiRepository } from "../repositories/repositoryBuilders.js";
import ForecastConfiguration from "../../models/forecast/template/ForecastConfiguration.js";
import ForecastTemplate from "../../models/forecast/template/ForecastTemplate.js";
import ReportTemplateForForecastingService from "./ReportTemplateForForecastingService.js";

/**
 * Service doing crud operation on instances of `ForecastTemplate`.
 *
 * @param {Company} company - The company for which forecast templates
 *   are handled
 * @param {string} countryId - The country `company` belongs to
 */
export default class ForecastTemplateService {
	constructor({ company, countryId }) {
		this._company = company;

		this._forecastTemplateRepository = makeComputationApiRepository({
			route: "ForecastTemplates/?id",
			model: ForecastTemplate,
			defaultFilters: {
				countryId,
				agencyId: company.getAgencyId(),
				companyId: company.getLegacyId(),
			},
		});

		this._reportTemplateRepository = makeComputationApiRepository({
			route: "ForecastTemplates/#forecastTemplateId/ReportTemplates/?id",
			defaultFilters: {
				countryId,
				agencyId: company.getAgencyId(),
				companyId: company.getLegacyId(),
			},
		});

		this._defaultForecastTemplateConfigurationRepository =
			makeComputationApiRepository({
				route: "DefaultForecastTemplateConfiguration",
				model: ForecastConfiguration,
				defaultFilters: {
					countryId,
					agencyId: company.getAgencyId(),
					companyId: company.getLegacyId(),
				},
			});

		this._forecastTemplateConfigurationRepository =
			makeComputationApiRepository({
				route: "ForecastTemplates/#forecastTemplateId/Configuration",
				model: ForecastConfiguration,
				defaultFilters: {
					countryId,
					agencyId: company.getAgencyId(),
					companyId: company.getLegacyId(),
				},
			});

		this._reportTemplateService = new ReportTemplateForForecastingService({
			reportTemplateRepository: this._reportTemplateRepository,
		});
	}

	create(spec) {
		return this._forecastTemplateRepository.save(spec);
	}

	async get(forecastTemplateId) {
		const [incomeStatement, balanceSheet, cashFlow] =
			await this._reportTemplateService.getReportTemplates({
				filter: { forecastTemplateId },
			});

		return this._forecastTemplateRepository.get({
			filter: { id: forecastTemplateId },
			context: {
				reportTemplates: { incomeStatement, balanceSheet, cashFlow },
			},
		});
	}

	getDefaultForecastTemplatesConfiguration({ chartOfAccountsId }) {
		return this._defaultForecastTemplateConfigurationRepository
			.get({
				notify: false,
				filter: { chartOfAccountsId },
			})
			.catch(() => new ForecastConfiguration());
	}

	updateDefaultForecastTemplatesConfiguration(forecastTemplateConfiguration) {
		return this._defaultForecastTemplateConfigurationRepository.update({
			model: forecastTemplateConfiguration,
			notify: false,
			readResponse: false,
		});
	}

	updateForecastTemplateConfiguration(
		forecastTemplateConfiguration,
		forecastTemplateId,
		notify = true,
	) {
		return this._forecastTemplateConfigurationRepository.update({
			model: forecastTemplateConfiguration,
			readResponse: false,
			filter: { forecastTemplateId },
			notify,
			successMessage: _("The configuration is successfully updated"),
			errorMessage: _("Failed to update the configuration"),
		});
	}

	getAll() {
		return this._forecastTemplateRepository.get();
	}
}
